import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import './AdminPages.css';

const CarModels = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    year_range: '',
    is_active: true,
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [reorderSaving, setReorderSaving] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchModels();
  }, []);

  useEffect(() => {
    if (location.state?.openAddForm) {
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openAddForm, location.pathname, navigate]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCarModels();
      // التعامل مع الاستجابة سواء كانت paginated أو مباشرة
      const data = response.data;
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && data.results && Array.isArray(data.results)) list = data.results;
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setModels(list);
    } catch (err) {
      console.error('Error fetching models:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || 'فشل تحميل الموديلات';
      alert(errorMessage);
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      // إنشاء معاينة للصورة
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('name', formData.name);
      if (formData.slug && formData.slug.trim()) {
        data.append('slug', formData.slug.trim());
      }
      data.append('year_range', formData.year_range);
      data.append('is_active', formData.is_active);
      if (formData.image) {
        data.append('image', formData.image);
      }

      if (editingModel) {
        const response = await adminAPI.updateCarModel(editingModel.id, data);
        console.log('Update response:', response.data);
        alert('تم تحديث الموديل بنجاح');
      } else {
        const response = await adminAPI.createCarModel(data);
        console.log('Create response:', response.data);
        alert('تم إضافة الموديل بنجاح');
      }

      resetForm();
      // إعادة جلب البيانات بعد الحفظ
      await fetchModels();
    } catch (err) {
      console.error('Error saving model:', err);
      console.error('Error details:', err.response?.data);
      alert('فشل حفظ الموديل: ' + (err.response?.data?.error || err.message || 'خطأ غير معروف'));
    }
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setFormData({
      name: model.name || '',
      slug: model.slug || '',
      year_range: model.year_range || '',
      is_active: model.is_active !== undefined ? model.is_active : true,
      image: null,
    });
    setImagePreview(model.image_url || null);
    setShowForm(true);
  };

  const handleMove = async (index, direction) => {
    if (direction === 'up' && index <= 0) return;
    if (direction === 'down' && index >= models.length - 1) return;
    const newList = [...models];
    const swap = direction === 'up' ? index - 1 : index + 1;
    [newList[index], newList[swap]] = [newList[swap], newList[index]];
    const modelIds = newList.map((m) => m.id).filter((id) => id != null);
    if (modelIds.length === 0) return;
    setModels(newList);
    setReorderSaving(true);
    try {
      await adminAPI.reorderCarModels(modelIds);
    } catch (err) {
      setModels(models);
      console.error('Reorder error:', err);
      alert(err.response?.data?.error || err.response?.data?.detail || err.message || 'فشل تحديث الترتيب');
    } finally {
      setReorderSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الموديل؟')) return;
    
    try {
      await adminAPI.deleteCarModel(id);
      alert('تم حذف الموديل بنجاح');
      fetchModels();
    } catch (err) {
      console.error('Error deleting model:', err);
      alert('فشل حذف الموديل');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      year_range: '',
      is_active: true,
      image: null,
    });
    setImagePreview(null);
    setEditingModel(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>إدارة الموديلات</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          إضافة موديل جديد
        </button>
      </div>

      {showForm && (
        <div className="admin-modal-backdrop" onClick={resetForm}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingModel ? 'تعديل الموديل' : 'إضافة موديل جديد'}</h2>
              <button type="button" className="admin-modal-close" onClick={resetForm} aria-label="إغلاق">×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-container">
                <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label>اسم الموديل *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>المسار في الرابط (slug)</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                placeholder="يُولَّد تلقائياً إن تُرك فارغاً عند الحفظ"
                dir="ltr"
              />
            </div>

            <div className="form-group">
              <label>نطاق السنوات</label>
              <input
                type="text"
                name="year_range"
                value={formData.year_range}
                onChange={handleInputChange}
                placeholder="مثال: 2002-2009"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                نشط
              </label>
            </div>

            <div className="form-group">
              <label>صورة الموديل</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              {(imagePreview || (editingModel && editingModel.image_url)) && (
                <div className="current-image" style={{ marginTop: '10px' }}>
                  <img 
                    src={imagePreview || editingModel.image_url} 
                    alt="Preview" 
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }} 
                  />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingModel ? 'تحديث' : 'إضافة'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                إلغاء
              </button>
            </div>
          </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <p className="admin-models-note">
        الموديلات التي تظهر في الصفحة الرئيسية للموقع هي فقط <strong>النشطة</strong> والتي لديها <strong>سيارة واحدة نشطة على الأقل</strong>. غير النشطة أو التي بدون سيارات نشطة تبقى هنا للإدارة ولا تظهر للزوار.
      </p>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>الترتيب</th>
              <th>الصورة</th>
              <th>الاسم</th>
              <th>نطاق السنوات</th>
              <th>عدد السيارات</th>
              <th>الحالة</th>
              <th>يظهر في الموقع</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {models.length === 0 ? (
              <tr className="empty-row">
                <td colSpan="8">لا توجد موديلات</td>
              </tr>
            ) : (
              models.map((model, index) => {
                const visibleOnSite = model.is_active && (model.variants_count || 0) >= 1;
                return (
                <tr key={model.id}>
                  <td data-label="الترتيب" className="td-reorder">
                    <button type="button" className="btn btn-sm btn-icon btn-reorder" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMove(index, 'up'); }} disabled={index === 0 || reorderSaving} title="تحريك لأعلى" aria-label="تحريك لأعلى">
                      <i className="fas fa-chevron-up" />
                    </button>
                    <button type="button" className="btn btn-sm btn-icon btn-reorder" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMove(index, 'down'); }} disabled={index === models.length - 1 || reorderSaving} title="تحريك لأسفل" aria-label="تحريك لأسفل">
                      <i className="fas fa-chevron-down" />
                    </button>
                    {reorderSaving && <span className="reorder-saving-label">حفظ...</span>}
                  </td>
                  <td data-label="الصورة">
                    {model.image_url ? (
                      <img 
                        src={model.image_url} 
                        alt={model.name} 
                        className="table-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling?.remove();
                          const span = document.createElement('span');
                          span.className = 'no-image';
                          span.textContent = 'خطأ في تحميل الصورة';
                          e.target.parentNode.appendChild(span);
                        }}
                      />
                    ) : (
                      <span className="no-image">—</span>
                    )}
                  </td>
                  <td data-label="الاسم">{model.name}</td>
                  <td data-label="نطاق السنوات">{model.year_range || '—'}</td>
                  <td data-label="عدد السيارات">{model.variants_count || 0}</td>
                  <td data-label="الحالة">
                    <span className={`status-badge ${model.is_active ? 'active' : 'inactive'}`}>
                      {model.is_active ? 'نشط' : 'غير نشط'}
                    </span>
                  </td>
                  <td data-label="يظهر في الموقع">
                    <span className={`status-badge ${visibleOnSite ? 'active' : 'inactive'}`} title={visibleOnSite ? 'يظهر في الصفحة الرئيسية' : 'لا يظهر: إما غير نشط أو لا توجد سيارة نشطة'}>
                      {visibleOnSite ? 'نعم' : 'لا'}
                    </span>
                  </td>
                  <td data-label="الإجراءات">
                    <div className="td-actions">
                      <button type="button" className="btn btn-sm btn-icon btn-edit" onClick={() => handleEdit(model)} title="تعديل" aria-label="تعديل">
                        <i className="fas fa-pen" />
                      </button>
                      <button type="button" className="btn btn-sm btn-icon btn-delete" onClick={() => handleDelete(model.id)} title="حذف" aria-label="حذف">
                        <i className="fas fa-trash-alt" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CarModels;
